import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMedicinesCatalog } from '../../medicines/context/MedicinesCatalogContext';
import PublicCartSidebar from '../components/PublicCartSidebar';
import PublicFooter from '../components/PublicFooter';
import PublicNavbar from '../components/PublicNavbar';
import { CATEGORIES, getCategoryIcon } from '../constants/catalog';
import { CartItem, PublicDeliveryInfo } from '../types/store';

type PublicProductsPageProps = {
  userName: string;
  deliveryInfo: PublicDeliveryInfo;
  cartItems: CartItem[];
  cartUnits: number;
  isCartOpen: boolean;
  onOpenCart: () => void;
  onCloseCart: () => void;
  onRemoveCartItem: (itemId: string) => void;
  onAddToCart: (payload: {
    id: number;
    name: string;
    unitPrice: number;
    quantity: number;
    icon?: string;
    image?: string;
  }) => void;
  onDeliveryChange: (delivery: PublicDeliveryInfo) => void;
  onLogout: () => void;
  onCheckout: () => void;
};

export default function PublicProductsPage({
  userName,
  deliveryInfo,
  cartItems,
  cartUnits,
  isCartOpen,
  onOpenCart,
  onCloseCart,
  onRemoveCartItem,
  onAddToCart,
  onDeliveryChange,
  onLogout,
  onCheckout,
}: PublicProductsPageProps) {
  const { medicines } = useMedicinesCatalog();
  const [productQuantities, setProductQuantities] = useState<Record<number, number>>({});

  const featuredProducts = useMemo(() => medicines.filter((product) => product.featured), [medicines]);

  const changeProductQuantity = (productId: number, delta: number) => {
    setProductQuantities((prev) => {
      const current = prev[productId] ?? 1;
      const next = Math.max(1, current + delta);
      return { ...prev, [productId]: next };
    });
  };

  return (
    <main className="public-page-shell">
      <PublicNavbar
        deliveryInfo={deliveryInfo}
        cartUnits={cartUnits}
        onOpenCart={onOpenCart}
        onDeliveryChange={onDeliveryChange}
      />

      <section className="card content-card public-home-card">
        <div className="public-greeting-block">
          <h2>Hola {userName}</h2>
          <p className="subtitle">Encuentra lo que necesitas para hoy.</p>
        </div>

        <div className="public-categories-grid" id="categorias">
          {CATEGORIES.map((category) => (
            <Link key={category.key} to={`/categoria/${category.key}`} className="public-category-card">
              <span className="public-category-icon" aria-hidden="true">
                {category.icon}
              </span>
              <p>{category.name}</p>
            </Link>
          ))}
        </div>

        {featuredProducts.length > 0 ? (
          <section className="public-products-section public-featured-section">
            <header className="public-products-header">
              <h3 className="section-title">Productos destacados</h3>
            </header>

            <div className="public-products-grid">
              {featuredProducts.map((product) => (
                <article key={product.id} className="public-product-card">
                  <div className="public-product-image">
                    {product.image ? (
                      <img src={product.image} alt={product.name} loading="lazy" />
                    ) : (
                      <span>{getCategoryIcon(product.categoryKey)}</span>
                    )}
                  </div>
                  <div className="public-product-info">
                    <p className="public-product-name">{product.name}</p>
                    {product.concentration ? (
                      <p className="public-product-concentration">{product.concentration}</p>
                    ) : null}
                    {product.presentation ? (
                      <p className="public-product-presentation">{product.presentation}</p>
                    ) : null}
                  </div>
                  <p className="public-product-price">S/ {product.unitPrice.toFixed(2)}</p>
                  <div className="product-qty-control">
                    <button type="button" className="qty-btn" onClick={() => changeProductQuantity(product.id, -1)}>
                      -
                    </button>
                    <span>{productQuantities[product.id] ?? 1}</span>
                    <button type="button" className="qty-btn" onClick={() => changeProductQuantity(product.id, 1)}>
                      +
                    </button>
                  </div>
                  <button
                    type="button"
                    className="add-to-cart-btn"
                    onClick={() =>
                      onAddToCart({
                        id: product.id,
                        name: product.name,
                        unitPrice: product.unitPrice,
                        quantity: productQuantities[product.id] ?? 1,
                        icon: getCategoryIcon(product.categoryKey),
                        image: product.image,
                      })
                    }
                  >
                    Agregar al carrito
                  </button>
                </article>
              ))}
            </div>
          </section>
        ) : null}
      </section>

      <PublicFooter onLogout={onLogout} />

      <PublicCartSidebar
        isOpen={isCartOpen}
        cartItems={cartItems}
        onClose={onCloseCart}
        onRemoveItem={onRemoveCartItem}
        onCheckout={onCheckout}
      />
    </main>
  );
}
